/**
 * @author 冷 (https://github.com/LengYXin)
 * @email lengyingxin8966@gmail.com
 * @create date 2021-09-09 09:33:53
 * @modify date 2021-09-09 09:33:53
 * @desc [用户信息]
 */
import lodash from 'lodash';
import { BindAll } from 'lodash-decorators';
import { computed } from 'mobx';
import { EnumAPI } from '../../apis/index';
import { EnumLoginType, EnumProtocol } from '../../enums';
import { AjaxBasics } from '../../helpers';
import { BaseModel } from '../bases/baseModel';
import { onCreateFactoryAuth } from './auth';
import { onCreateFactoryUser } from './userModel';

@BindAll()
export class AccountCenterController {
  /**
   * 用户信息
   * @memberof SystemController
   */
  readonly User = onCreateFactoryUser();
  readonly Model = new BaseModel({ type: 'object', storageKey: 'account' });
  readonly Auth = onCreateFactoryAuth();
  /** 用户名 */
  @computed
  public get username() {
    return lodash.get(this.Model.value, 'loginUsername');
  }
  @computed
  get lowPwdInfo() {
    return lodash.get(this.Model.value, 'lowPwdInfo');
  }
  @computed
  get isPwdOverTime() {
    return !lodash.get(this.lowPwdInfo, 'idToken');
  }
  @computed
  get bindMobile() {
    return lodash.get(this.Model.value, 'bindMobile');
  }

  @computed
  get ignoreBind() {
    return lodash.get(this.Model.value, 'ignoreBind');
  }

  /**
   * 小程序改造为账号中心登陆
   * @param {*} formData
   * @return {*}
   * @memberof SystemController
   */
  async onLoginForWeapp(body: {
    loginType?;
    username?;
    password?;
    verificationCode?;
    imgSession?;
    mobile?;
    imgCode?;
  }) {
    try {
      // this.User.toggleLoading(true);
      let res = null; //登录成功后应返回 idToken + sourceAppId
      if (lodash.eq(lodash.get(body, 'loginType'), EnumLoginType.mobile)) {
        res = await AjaxBasics.request(
          lodash.merge({}, EnumAPI.accountsvr_sms_login, { body }),
        );
      }
      if (lodash.eq(lodash.get(body, 'loginType'), EnumLoginType.registered)) {
        res = await AjaxBasics.request({
          ...EnumAPI.accountsvr_user_add_login,
          body: { ...lodash.get(this.Model.value, 'registerInfo') },
        });
        // res = lodash.get(this.Model.value, 'registerInfo')
      }
      // if (lodash.eq(lodash.get(body, 'loginType'), EnumLoginType.account)) {
      //     res = await AjaxBasics.request(lodash.merge({}, EnumAPI.accountsvr_account_login, { body, headers: { 'img-session': lodash.get(body, 'imgSession') } }))
      // }
      if (lodash.eq(lodash.get(body, 'loginType'), EnumLoginType.account)) {
        if (lodash.get(body, 'accountRes')) {
          res = this.lowPwdInfo;
          this.Model.merge({ lowPwdInfo: null });
        } else {
          res = await AjaxBasics.request(
            lodash.merge({}, EnumAPI.accountsvr_account_login, {
              body,
              headers: { 'img-session': lodash.get(body, 'imgSession') },
            }),
          );
          if (lodash.get(res, 'updatePwdByLoginCode')) {
            res = { lowPwdInfo: res };
          }
        }
      }
      const { idToken } = res;
      // this.User.merge({ token: idToken });
      this.Auth.onSaveAccessToken(idToken);
      this.Model.merge(res);

      // 保存协议记录
      // 登录后需要绑定手机号的话先不调用这个接口,不然报401，直接跳转登录页了
      try {
        if (
          lodash.includes(
            [EnumLoginType.mobile, EnumLoginType.account],
            lodash.get(body, 'loginType'),
          ) &&
          !this.bindMobile
        ) {
          await this.Auth.HydrateAsync;
          await this.onSaveProtocol({
            sceneCode: EnumProtocol.login,
          });
        }
      } catch (error) {}
      // await this.onGetUserInfo()
      return true;
    } catch (error) {
      // this.User.toggleLoading(false);
      console.log('LENG ~ SystemController ~ onLogin ~ this', this);
      throw error;
    }
  }

  /**
   * 小程序改为账号中心微信登陆
   */
  async onAuth(body) {
    const res = await AjaxBasics.request({
      ...EnumAPI.accountsvr_wechat_login,
      body,
      headers: { 'x-wechat-app-id': lodash.get(body, 'wxAppId') },
    });
    let userTokenResponse = lodash.get(res, 'userTokenResponse');
    if (userTokenResponse) {
      const { idToken } = userTokenResponse;
      // this.User.merge({ token: idToken });
      this.Auth.onSaveAccessToken(idToken);
      this.Model.merge(userTokenResponse);
    } else {
      this.Model.merge({ bindToken: lodash.get(res, 'bindToken') });
    }
    return res;
  }

  /** 小程序改造为账号中心注册 */
  async registerUserForWeapp(body) {
    const registerLoginCode: any = await AjaxBasics.request({
      ...EnumAPI.accountsvr_user_add,
      body,
    });
    this.Model.merge({ registerInfo: registerLoginCode });
    // 保存协议记录
    this.onSaveProtocol({
      sceneCode: EnumProtocol.register,
      userCode: lodash.get(body, 'mobile'),
    });
  }

  /** 保存微信扩展信息 */
  wechatExtendSave(body) {
    return AjaxBasics.request({
      ...EnumAPI.accountsvr_wechat_extend_save,
      body: {
        extendInfoList: body,
      },
    });
  }

  /**
   * 获取协议，传sceneCode场景值获取，枚举值从EnumProtocol获取
   * @memberof ProtocolController
   */
  async getProtocolList(sceneCode) {
    try {
      const res: any[] = await AjaxBasics.request<any>({
        ...EnumAPI.legal_portal_protocol_find,
        body: { sceneCode },
      });
      this.Model.merge({ protocolList: res });
      return res;
    } catch (error) {
      throw error;
    }
  }

  /**
   * 确认协议
   * @memberof ProtocolController
   */
  async onSaveProtocol(body) {
    try {
      return AjaxBasics.request<any>({
        ...EnumAPI.legal_portal_protocol_save,
        body,
      });
    } catch (error) {
      console.log('error:', error);
      // throw error;
    }
  }
}
